import Link from "next/link"
import { useCallback, useEffect, useRef, useState } from "react"
import Footer from "../components/footer"
import Menu from "../components/menu"
import database from "../database.mjs"
import Fuse from "fuse.js"
import { useRouter } from "next/router"
import { NextSeo } from "next-seo"
import { useIsomorphicLayoutEffect } from "@react-spring/web"
import Case from "case"
import Map, { Marker, NavigationControl } from "react-map-gl"
import "mapbox-gl/dist/mapbox-gl.css"
import { IoMdPaw } from "react-icons/io"

export async function getServerSideProps({ query }) {
  const studentProperties = database
    .prepare("select * from properties_to_let where audience = ? order by price desc, beds desc limit 1000")
    .all("student")

  const professionalProperties = database
    .prepare("select * from properties_to_let where audience = ? order by price desc, beds desc limit 1000")
    .all("professional")

  const properties = [...professionalProperties, ...studentProperties]

  for (const property of properties) {
    const id = property?.["id"]
    const filename = database
      .prepare("select filename from properties_to_let_photos where property_id = ? order by id asc limit 1")
      .get(id)?.["filename"]

    property["thumbnail"] = `/api/images/${filename}.webp`
    property["description"] = Case.sentence(`${property?.description?.slice(0, 150)?.trimEnd()}...`)
  }

  return {
    props: {
      properties,
      area: query?.area ? decodeURI(query?.area) : null,
      search: query?.search ? decodeURI(query?.search) : null,
    },
  }
}

function Rent({ properties, area, search: _search }) {
  const smallerFuse = useCallback(
    new Fuse(properties, {
      keys: ["area"],
      includeMatches: false,
      threshold: 0,
    }),
    []
  )

  const fuse = useCallback(
    new Fuse(properties, {
      keys: ["address", "area"],
      includeMatches: false,
      threshold: 0.25,
    }),
    []
  )

  const [scrollPosition, setScrollPosition] = useState(0)
  const [windowInnerHeight, setWindowInnerHeight] = useState(0)

  const mapButtonsContainer = useRef(null)
  const mapButtonRef = useRef(null)

  useEffect(() => {
    document?.body?.classList?.remove("overflow-hidden")
  }, [])

  useIsomorphicLayoutEffect(() => {
    setScrollPosition(window?.scrollY)
    setWindowInnerHeight(window?.innerHeight)
  }, [])

  useEffect(() => {
    const listener = () => {
      setScrollPosition(window?.scrollY)
      setWindowInnerHeight(window?.innerHeight)
    }

    window?.addEventListener("resize", listener)
    window?.addEventListener("scroll", listener)

    return () => {
      window?.removeEventListener("resize", listener)
      window?.removeEventListener("scroll", listener)
    }
  }, [])

  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [filteredProperties, setFilteredProperties] = useState(
    area
      ? smallerFuse.search(area).map(result => result.item)
      : _search
      ? fuse.search(_search).map(result => result.item)
      : properties
  )

  useEffect(() => {
    const handleKeyPress = event => {
      if (selectedIndex !== -1) {
        if (event.keyCode === 38) {
          event.preventDefault()

          const newIndex = Math.max(0, selectedIndex - 1)
          setSelectedIndex(newIndex)

          const property = filteredProperties[newIndex]
          mapRef?.current?.panTo([property?.long, property?.lat], { zoom: 13 })

          const newY =
            document.getElementById(`button-${newIndex}`).getBoundingClientRect().y -
            document.getElementById(`map-buttons-container`).getBoundingClientRect().top +
            92

          window.scrollTo({ top: newY, behavior: "instant" })
        } else if (event.keyCode === 40) {
          event.preventDefault()

          const newIndex = Math.min(filteredProperties.length - 1, selectedIndex + 1)
          setSelectedIndex(newIndex)

          const property = filteredProperties[newIndex]
          mapRef?.current?.panTo([property?.long, property?.lat], { zoom: 13 })

          const newY =
            document.getElementById(`button-${newIndex}`).getBoundingClientRect().y -
            document.getElementById(`map-buttons-container`).getBoundingClientRect().top +
            92

          window.scrollTo({ top: newY, behavior: "instant" })
        }
      }
    }

    document.addEventListener("keydown", handleKeyPress)

    return () => {
      document.removeEventListener("keydown", handleKeyPress)
    }
  }, [selectedIndex, filteredProperties])

  const mapRef = useRef()

  const [showFiltersButton, setShowFiltersButton] = useState(false)
  const [showRecenter, setShowRecenter] = useState(false)

  const [view, setView] = useState("grid")

  const [priceFilter, setPriceFilter] = useState(Infinity)
  const [bedsFilter, setBedsFilter] = useState(0)
  const [sortFilter, setSortFilter] = useState("")

  const [typeFilter, setTypeFilter] = useState("")

  const priceSelectRef = useRef(null)
  const maxPrice = Math.max(...properties.map(property => property.price))
  const maxBeds = Math.max(...properties.map(property => property.beds))

  const [search, setSearch] = useState(_search || "")

  const router = useRouter()

  const typeSelectRef = useRef(null)

  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const searchInputRef = useRef(null)

  useEffect(() => {
    const handle = setTimeout(() => {
      setIsSearchOpen(false)
      navigator?.virtualKeyboard?.hide()
      searchInputRef?.current?.blur()
    }, 3500)

    return () => clearTimeout(handle)
  }, [search])

  const [mapHeight, setMapHeight] = useState(0)

  useEffect(() => {
    setMapHeight(
      document.getElementById("map-container")?.getBoundingClientRect()?.height -
        document.getElementById("button-0")?.getBoundingClientRect()?.height || 0
    )
  })

  let indexesOfFirstAd = [11]
  for (let i = 1; i <= 500; i++) {
    if (i % 44 === 0) {
      indexesOfFirstAd.push(i)
    }
  }
  indexesOfFirstAd = indexesOfFirstAd.map(index => index - 1)

  return (
    <>
      <NextSeo
        title={area ? `Properties for rent in ${area}` : "Properties for rent"}
        description={`Find your next rental property with us. We have${
          area ? ` ${filteredProperties.length}` : " a range of"
        } properties available to rent${area ? ` in ${area}` : ""}. Contact us today to arrange a viewing.`}
      />
      <div className="bg-white">
        <div className="flex flex-col min-h-screen px-4 pt-4 sm:pt-24">
          <div className="flex items-center w-full p-3 mx-auto overflow-x-scroll rounded pointer-events-auto gap-x-2 bg-cpink sm:max-w-screen-sm md:max-w-screen-md lg:max-w-screen-lg xl:max-w-screen-xl 2xl:max-w-screen-2xl text-stone-900 no-scrollbar">
            <button
              aria-label="Search"
              onClick={() => {
                if (isSearchOpen) return

                setIsSearchOpen(isSearchOpen => !isSearchOpen)
                if (!isSearchOpen) {
                  setTimeout(() => {
                    searchInputRef?.current?.focus()
                    navigator?.virtualKeyboard?.show()
                    document?.querySelector("search-bar-input")?.focus()
                  }, 350)
                }
              }}
              className={`flex lg:hidden items-center px-3 min-w-[8rem] xl:min-w-[10rem] ${
                isSearchOpen ? "py-2" : "py-3 min-w-fit"
              } rounded bg-white text-stone-900 gap-x-[.375rem] h-full`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path
                  fillRule="evenodd"
                  d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z"
                  clipRule="evenodd"
                />
              </svg>
              <input
                onClick={() => setSelectedIndex(-1)}
                disabled={!isSearchOpen}
                id="search-bar-input"
                onBlur={() => {
                  setIsSearchOpen(false)
                }}
                ref={searchInputRef}
                className={[
                  "flex-1 bg-transparent outline-none text-stone-900 placeholder:text-stone-900 hover:cursor-text overflow-hidden w-full text-base transition-all duration-300",
                  `${isSearchOpen ? "block" : "hidden"}`,
                ].join(" ")}
                placeholder="Search"
                onChange={e => {
                  const { value } = e.target
                  const newSearch = value.trimStart()

                  if (newSearch.length === 0) {
                    delete router.query.area
                    delete router.query.search
                  } else {
                    delete router.query.area
                    router.query.search = encodeURIComponent(newSearch)
                  }

                  router.replace(router, undefined, { shallow: true })

                  setSearch(newSearch)

                  if (newSearch.length === 0) {
                    let results = properties.filter(property => property.beds >= bedsFilter && property.price <= priceFilter)

                    if (sortFilter) {
                      if (sortFilter === "price highest") {
                        results = results.sort((a, b) => b.price - a.price)
                      } else if (sortFilter === "price lowest") {
                        results = results.sort((a, b) => b.price - a.price).reverse()
                      } else if (sortFilter === "recent") {
                        results = results.sort((a, b) => b.timestamp.localeCompare(a.timestamp))
                      }
                    }

                    if (typeFilter !== "") results = results.filter(result => result.audience === typeFilter)

                    setFilteredProperties(results)

                    return
                  }

                  let results = fuse
                    .search(newSearch)
                    .map(result => result.item)
                    .filter(property => property.beds >= bedsFilter && property.price <= priceFilter)

                  if (sortFilter) {
                    if (sortFilter === "price highest") {
                      results = results.sort((a, b) => b.price - a.price)
                    } else if (sortFilter === "price lowest") {
                      results = results.sort((a, b) => b.price - a.price).reverse()
                    } else if (sortFilter === "recent") {
                      results = results.sort((a, b) => b.timestamp.localeCompare(a.timestamp))
                    }
                  }

                  if (typeFilter !== "") results = results.filter(result => result.audience === typeFilter)

                  setFilteredProperties(results)
                }}
                value={search}
              />
            </button>
            <div className="hidden lg:flex items-center px-3 py-2 rounded bg-white text-stone-900 gap-x-[.375rem] h-full">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path
                  fillRule="evenodd"
                  d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z"
                  clipRule="evenodd"
                />
              </svg>
              <input
                className={[
                  "flex-1 bg-transparent outline-none text-stone-900 placeholder:text-stone-900 hover:cursor-text overflow-hidden w-full text-base",
                ]}
                onClick={() => setSelectedIndex(-1)}
                placeholder="Search"
                onChange={e => {
                  const { value } = e.target
                  const newSearch = value.trimStart()

                  if (newSearch.length === 0) {
                    delete router.query.area
                    delete router.query.search
                  } else {
                    delete delete router.query.area
                    router.query.search = encodeURIComponent(newSearch)
                  }

                  router.replace(router, undefined, { shallow: true })

                  setSearch(newSearch)

                  if (newSearch.length === 0) {
                    let results = properties.filter(property => property.beds >= bedsFilter && property.price <= priceFilter)

                    if (sortFilter) {
                      if (sortFilter === "price highest") {
                        results = results.sort((a, b) => b.price - a.price)
                      } else if (sortFilter === "price lowest") {
                        results = results.sort((a, b) => b.price - a.price).reverse()
                      } else if (sortFilter === "recent") {
                        results = results.sort((a, b) => b.timestamp - a.timestamp)
                      }
                    }

                    setFilteredProperties(results)

                    return
                  }

                  let results = fuse
                    .search(newSearch)
                    .map(result => result.item)
                    .filter(property => property.beds >= bedsFilter && property.price <= priceFilter)

                  if (sortFilter) {
                    if (sortFilter === "price highest") {
                      results = results.sort((a, b) => b.price - a.price)
                    } else if (sortFilter === "price lowest") {
                      results = results.sort((a, b) => b.price - a.price).reverse()
                    } else if (sortFilter === "recent") {
                      results = results.sort((a, b) => b.timestamp - a.timestamp)
                    }
                  }

                  setFilteredProperties(results)
                }}
                value={search}
              />
            </div>
            <div className="relative flex items-center w-32 flex-1 min-w-[7rem] xl:flex-none xl:min-w-[10rem] px-4 py-2 rounded gap-x-1 bg-white">
              {bedsFilter === 0 && (
                <div className="absolute top-0 bottom-0 left-0 right-0 flex items-center px-4 py-2 bg-transparent rounded pointer-events-none gap-x-1 ">
                  <p className="bg-white">Min Beds</p>
                </div>
              )}
              <select
                aria-label="Min beds"
                className={`bg-transparent appearance-none focus:outline-none w-full ${
                  bedsFilter === 0 ? "" : "text-stone-900"
                }`}
                onChange={e => {
                  const { value } = e.target
                  const newSearch = value.trimStart()

                  if (newSearch.length === 0) {
                    delete router.query.area
                    delete router.query.search
                  } else {
                    delete router.query.area
                    router.query.search = encodeURIComponent(newSearch)
                  }

                  router.replace(router, undefined, { shallow: true })

                  setSearch(newSearch)

                  if (newSearch.length === 0) {
                    let results = properties.filter(property => property.beds >= bedsFilter && property.price <= priceFilter)

                    if (sortFilter) {
                      if (sortFilter === "price highest") {
                        results = results.sort((a, b) => b.price - a.price)
                      } else if (sortFilter === "price lowest") {
                        results = results.sort((a, b) => b.price - a.price).reverse()
                      } else if (sortFilter === "recent") {
                        results = results.sort((a, b) => b.timestamp.localeCompare(a.timestamp))
                      }
                    }

                    if (typeFilter !== "") results = results.filter(result => result.audience === typeFilter)

                    setFilteredProperties(results)

                    return
                  }

                  let results = fuse
                    .search(newSearch)
                    .map(result => result.item)
                    .filter(property => property.beds >= bedsFilter && property.price <= priceFilter)

                  if (sortFilter) {
                    if (sortFilter === "price highest") {
                      results = results.sort((a, b) => b.price - a.price)
                    } else if (sortFilter === "price lowest") {
                      results = results.sort((a, b) => b.price - a.price).reverse()
                    } else if (sortFilter === "recent") {
                      results = results.sort((a, b) => b.timestamp.localeCompare(a.timestamp))
                    }
                  }

                  if (typeFilter !== "") results = results.filter(result => result.audience === typeFilter)

                  setFilteredProperties(results)
                }}
                value={bedsFilter}
              >
                <option value={1}>1</option>
                <option value={2}>2</option>
                <option value={3}>3</option>
                <option value={4}>4</option>
                <option value={5}>5</option>
                {maxBeds >= 6 && <option value={6}>6</option>}
              </select>
              <select
                aria-label="Min beds"
                className={`bg-transparent absolute top-0 left-0 bottom-0 right-0 opacity-0 appearance-none focus:outline-none w-full ${
                  bedsFilter === 0 ? "" : "text-stone-900"
                }`}
                onChange={event => {
                  const { value } = event.target
                  setBedsFilter(value)

                  let results

                  if (area) {
                    results = smallerFuse.search(area).map(result => result.item)
                  } else {
                    results = fuse.search(search).map(result => result.item)
                  }

                  if (results.length === 0) {
                    results = properties.filter(property => property.beds >= value && property.price <= priceFilter)

                    if (sortFilter) {
                      if (sortFilter === "price highest") {
                        results = results.sort((a, b) => b.price - a.price)
                      } else if (sortFilter === "price lowest") {
                        results = results.sort((a, b) => b.price - a.price).reverse()
                      } else if (sortFilter === "recent") {
                        results = results.sort((a, b) => b.timestamp.localeCompare(a.timestamp))
                      }
                    }

                    if (typeFilter !== "") results = results.filter(result => result.audience === typeFilter)

                    setFilteredProperties(results)

                    return
                  }

                  results = results.filter(property => property.beds >= value && property.price <= priceFilter)

                  if (sortFilter) {
                    if (sortFilter === "price highest") {
                      results = results.sort((a, b) => b.price - a.price)
                    } else if (sortFilter === "price lowest") {
                      results = results.sort((a, b) => b.price - a.price).reverse()
                    } else if (sortFilter === "recent") {
                      results = results.sort((a, b) => b.timestamp.localeCompare(a.timestamp))
                    }
                  }

                  if (typeFilter !== "") results = results.filter(result => result.audience === typeFilter)

                  setFilteredProperties(results)
                }}
                value={bedsFilter}
              >
                <option value={1}>1</option>
                <option value={2}>2</option>
                <option value={3}>3</option>
                <option value={4}>4</option>
                <option value={5}>5</option>
                {maxBeds >= 6 && <option value={6}>6</option>}
              </select>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 -mr-2">
                <path
                  fillRule="evenodd"
                  d="M10 3a.75.75 0 01.55.24l3.25 3.5a.75.75 0 11-1.1 1.02L10 4.852 7.3 7.76a.75.75 0 01-1.1-1.02l3.25-3.5A.75.75 0 0110 3zm-3.76 9.2a.75.75 0 011.06.04l2.7 2.908 2.7-2.908a.75.75 0 111.1 1.02l-3.25 3.5a.75.75 0 01-1.1 0l-3.25-3.5a.75.75 0 01.04-1.06z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="relative flex items-center flex-1 min-w-[7rem] xl:flex-none xl:min-w-[10rem] px-4 py-2 rounded gap-x-1 bg-white">
              {priceFilter === Infinity && (
                <div className="absolute top-0 bottom-0 left-0 right-0 flex items-center px-4 py-2 bg-transparent rounded pointer-events-none gap-x-1 ">
                  <p className="bg-white">Max Rent</p>
                </div>
              )}
              <select
                aria-label="Max rent"
                ref={priceSelectRef}
                className={`bg-transparent appearance-none focus:outline-none w-full ${
                  priceFilter === Infinity ? "" : "text-stone-900"
                }`}
                onChange={event => {
                  const { value } = event.target
                  setPriceFilter(value)

                  let results

                  if (area) {
                    results = smallerFuse.search(area).map(result => result.item)
                  } else {
                    results = fuse.search(search).map(result => result.item)
                  }

                  if (results.length === 0) {
                    results = properties.filter(property => property.beds >= bedsFilter && property.price <= value)

                    if (sortFilter) {
                      if (sortFilter === "price highest") {
                        results = results.sort((a, b) => b.price - a.price)
                      } else if (sortFilter === "price lowest") {
                        results = results.sort((a, b) => b.price - a.price).reverse()
                      } else if (sortFilter === "recent") {
                        results = results.sort((a, b) => b.timestamp.localeCompare(a.timestamp))
                      }
                    }

                    if (typeFilter !== "") results = results.filter(result => result.audience === typeFilter)

                    setFilteredProperties(results)

                    return
                  }

                  results = results.filter(property => property.beds >= bedsFilter && property.price <= value)

                  if (sortFilter) {
                    if (sortFilter === "price highest") {
                      results = results.sort((a, b) => b.price - a.price)
                    } else if (sortFilter === "price lowest") {
                      results = results.sort((a, b) => b.price - a.price).reverse()
                    } else if (sortFilter === "recent") {
                      results = results.sort((a, b) => b.timestamp.localeCompare(a.timestamp))
                    }
                  }

                  if (typeFilter !== "") results = results.filter(result => result.audience === typeFilter)

                  setFilteredProperties(results)
                }}
                value={priceFilter}
              >
                <option value={1500}>£2,500</option>
                <option value={1500}>£2,000</option>
                <option value={1500}>£1,500</option>
                <option value={1000}>£1,000</option>
                <option value={900}>£900</option>
                <option value={800}>£800</option>
                <option value={700}>£700</option>
                <option value={600}>£600</option>
                <option value={500}>£500</option>
                <option value={400}>£400</option>
              </select>
              <select
                aria-label="Max rent"
                ref={priceSelectRef}
                className={`bg-transparent appearance-none absolute opacity-0 top-0 left-0 right-0 bottom-0 z-20 focus:outline-none w-full ${
                  priceFilter === Infinity ? "" : "text-stone-900"
                }`}
                onChange={event => {
                  const { value } = event.target
                  setPriceFilter(value)

                  let results

                  if (area) {
                    results = smallerFuse.search(area).map(result => result.item)
                  } else {
                    results = fuse.search(search).map(result => result.item)
                  }

                  if (results.length === 0) {
                    results = properties.filter(property => property.beds >= bedsFilter && property.price <= value)

                    if (sortFilter) {
                      if (sortFilter === "price highest") {
                        results = results.sort((a, b) => b.price - a.price)
                      } else if (sortFilter === "price lowest") {
                        results = results.sort((a, b) => b.price - a.price).reverse()
                      } else if (sortFilter === "recent") {
                        results = results.sort((a, b) => b.timestamp.localeCompare(a.timestamp))
                      }
                    }

                    if (typeFilter !== "") results = results.filter(result => result.audience === typeFilter)

                    setFilteredProperties(results)

                    return
                  }

                  results = results.filter(property => property.beds >= bedsFilter && property.price <= value)

                  if (sortFilter) {
                    if (sortFilter === "price highest") {
                      results = results.sort((a, b) => b.price - a.price)
                    } else if (sortFilter === "price lowest") {
                      results = results.sort((a, b) => b.price - a.price).reverse()
                    } else if (sortFilter === "recent") {
                      results = results.sort((a, b) => b.timestamp.localeCompare(a.timestamp))
                    }
                  }

                  if (typeFilter !== "") results = results.filter(result => result.audience === typeFilter)

                  setFilteredProperties(results)
                }}
                value={priceFilter}
              >
                <option value={1500}>£2,500</option>
                <option value={1500}>£2,000</option>
                <option value={1500}>£1,500</option>
                <option value={1000}>£1,000</option>
                <option value={900}>£900</option>
                <option value={800}>£800</option>
                <option value={700}>£700</option>
                <option value={600}>£600</option>
                <option value={500}>£500</option>
                <option value={400}>£400</option>
              </select>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 -mr-2">
                <path
                  fillRule="evenodd"
                  d="M10 3a.75.75 0 01.55.24l3.25 3.5a.75.75 0 11-1.1 1.02L10 4.852 7.3 7.76a.75.75 0 01-1.1-1.02l3.25-3.5A.75.75 0 0110 3zm-3.76 9.2a.75.75 0 011.06.04l2.7 2.908 2.7-2.908a.75.75 0 111.1 1.02l-3.25 3.5a.75.75 0 01-1.1 0l-3.25-3.5a.75.75 0 01.04-1.06z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="relative hidden md:flex items-center w-32 min-w-[8rem] xl:min-w-[10rem] px-4 py-2 rounded gap-x-1 bg-white">
              {sortFilter === "" && (
                <div className="absolute top-0 bottom-0 left-0 right-0 flex items-center px-4 py-2 bg-transparent rounded pointer-events-none gap-x-1">
                  <p className="w-[5.5rem] bg-white">Sort</p>
                </div>
              )}
              <select
                aria-label="Sort"
                className={`bg-transparent appearance-none focus:outline-none w-full ${
                  sortFilter === "" ? "" : "text-stone-900"
                }`}
                onChange={event => {
                  const { value } = event.target
                  setSortFilter(value)

                  let results

                  if (area) {
                    results = smallerFuse.search(area).map(result => result.item)
                  } else {
                    results = fuse.search(search).map(result => result.item)
                  }

                  if (results.length === 0) {
                    results = properties.filter(property => property.beds >= bedsFilter && property.price <= priceFilter)

                    if (value) {
                      if (value === "price highest") {
                        results = results.sort((a, b) => b.price - a.price)
                      } else if (value === "price lowest") {
                        results = results.sort((a, b) => b.price - a.price).reverse()
                      } else if (value === "recent") {
                        results = results.sort((a, b) => b.timestamp.localeCompare(a.timestamp))
                      }
                    }

                    if (typeFilter !== "") results = results.filter(result => result.audience === typeFilter)

                    setFilteredProperties(results)

                    return
                  }

                  results = results.filter(property => property.beds >= bedsFilter && property.price <= priceFilter)

                  if (value) {
                    if (value === "price highest") {
                      results = results.sort((a, b) => b.price - a.price)
                    } else if (value === "price lowest") {
                      results = results.sort((a, b) => b.price - a.price).reverse()
                    } else if (value === "recent") {
                      results = results.sort((a, b) => b.timestamp.localeCompare(a.timestamp))
                    }
                  }

                  if (typeFilter !== "") results = results.filter(result => result.audience === typeFilter)

                  setFilteredProperties(results)
                }}
                value={sortFilter}
              >
                <option value={""} hidden>
                  None
                </option>
                <option value={"price highest"}>Highest Price</option>
                <option value={"price lowest"}>Lowest Price</option>
                <option value="recent">Recent</option>
              </select>
              <select
                aria-label="Sort"
                className={`bg-transparent appearance-none absolute z-20 opacity-0 focus:outline-none w-full ${
                  sortFilter === "" ? "" : "text-stone-900"
                }`}
                onChange={event => {
                  const { value } = event.target
                  setSortFilter(value)

                  let results

                  if (area) {
                    results = smallerFuse.search(area).map(result => result.item)
                  } else {
                    results = fuse.search(search).map(result => result.item)
                  }

                  if (results.length === 0) {
                    results = properties.filter(property => property.beds >= bedsFilter && property.price <= priceFilter)

                    if (value) {
                      if (value === "price highest") {
                        results = results.sort((a, b) => b.price - a.price)
                      } else if (value === "price lowest") {
                        results = results.sort((a, b) => b.price - a.price).reverse()
                      } else if (value === "recent") {
                        results = results.sort((a, b) => b.timestamp.localeCompare(a.timestamp))
                      }
                    }

                    if (typeFilter !== "") results = results.filter(result => result.audience === typeFilter)

                    setFilteredProperties(results)

                    return
                  }

                  results = results.filter(property => property.beds >= bedsFilter && property.price <= priceFilter)

                  if (value) {
                    if (value === "price highest") {
                      results = results.sort((a, b) => b.price - a.price)
                    } else if (value === "price lowest") {
                      results = results.sort((a, b) => b.price - a.price).reverse()
                    } else if (value === "recent") {
                      results = results.sort((a, b) => b.timestamp.localeCompare(a.timestamp))
                    }
                  }

                  if (typeFilter !== "") results = results.filter(result => result.audience === typeFilter)

                  setFilteredProperties(results)
                }}
                value={sortFilter}
              >
                <option value={""} hidden>
                  None
                </option>
                <option value={"price highest"}>Highest Price</option>
                <option value={"price lowest"}>Lowest Price</option>
                <option value="recent">Recent</option>
              </select>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 -mr-2">
                <path
                  fillRule="evenodd"
                  d="M10 3a.75.75 0 01.55.24l3.25 3.5a.75.75 0 11-1.1 1.02L10 4.852 7.3 7.76a.75.75 0 01-1.1-1.02l3.25-3.5A.75.75 0 0110 3zm-3.76 9.2a.75.75 0 011.06.04l2.7 2.908 2.7-2.908a.75.75 0 111.1 1.02l-3.25 3.5a.75.75 0 01-1.1 0l-3.25-3.5a.75.75 0 01.04-1.06z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="relative hidden md:flex items-center w-32 min-w-[8rem] xl:min-w-[10rem] px-4 py-2 rounded gap-x-1 bg-white">
              {typeFilter === "" && (
                <div className="absolute top-0 bottom-0 left-0 right-0 flex items-center px-4 py-2 bg-transparent rounded opacity-0 pointer-events-none gap-x-1">
                  <p className="w-[5.5rem] bg-white">Type</p>
                </div>
              )}
              <select
                aria-label="Type"
                tabIndex={-1}
                id="type-select"
                ref={typeSelectRef}
                className={`z-10 appearance-none w-full bg-white ${typeFilter === "" ? "" : "text-stone-900"}`}
                onChange={event => {
                  const { value } = event.target
                  setTypeFilter(value)

                  let results

                  if (area) {
                    results = smallerFuse.search(area).map(result => result.item)
                  } else {
                    results = fuse.search(search).map(result => result.item)
                  }

                  if (results.length === 0) {
                    results = properties.filter(property => property.beds >= bedsFilter && property.price <= priceFilter)

                    if (priceFilter) {
                      if (priceFilter === "price highest") {
                        results = results.sort((a, b) => b.price - a.price)
                      } else if (priceFilter === "price lowest") {
                        results = results.sort((a, b) => b.price - a.price).reverse()
                      } else if (priceFilter === "recent") {
                        results = results.sort((a, b) => b.timestamp.localeCompare(a.timestamp))
                      }
                    }

                    if (value !== "") results = results.filter(result => result.audience === value)

                    setFilteredProperties(results)

                    return
                  }

                  results = results.filter(property => property.beds >= bedsFilter && property.price <= priceFilter)

                  if (priceFilter) {
                    if (priceFilter === "price highest") {
                      results = results.sort((a, b) => b.price - a.price)
                    } else if (priceFilter === "price lowest") {
                      results = results.sort((a, b) => b.price - a.price).reverse()
                    } else if (priceFilter === "recent") {
                      results = results.sort((a, b) => b.timestamp.localeCompare(a.timestamp))
                    }
                  }

                  if (value !== "") results = results.filter(result => result.audience === value)

                  setFilteredProperties(results)
                }}
                value={typeFilter}
              >
                <option value="" hidden>
                  None
                </option>
                <option value="professional">Professional</option>
                <option value="student">Student</option>
              </select>
              <select
                aria-label="Type"
                tabIndex={-1}
                id="type-select"
                ref={typeSelectRef}
                className={`z-10 appearance-none absolute top-0 left-0 right-0 bottom-0 opacity-0 focus:outline-none w-full ${
                  typeFilter === "" ? "" : "text-stone-900"
                }`}
                onChange={event => {
                  const { value } = event.target
                  setTypeFilter(value)

                  let results

                  if (area) {
                    results = smallerFuse.search(area).map(result => result.item)
                  } else {
                    results = fuse.search(search).map(result => result.item)
                  }

                  if (results.length === 0) {
                    results = properties.filter(property => property.beds >= bedsFilter && property.price <= priceFilter)

                    if (priceFilter) {
                      if (priceFilter === "price highest") {
                        results = results.sort((a, b) => b.price - a.price)
                      } else if (priceFilter === "price lowest") {
                        results = results.sort((a, b) => b.price - a.price).reverse()
                      } else if (priceFilter === "recent") {
                        results = results.sort((a, b) => b.timestamp.localeCompare(a.timestamp))
                      }
                    }

                    if (value !== "") results = results.filter(result => result.audience === value)

                    setFilteredProperties(results)

                    return
                  }

                  results = results.filter(property => property.beds >= bedsFilter && property.price <= priceFilter)

                  if (priceFilter) {
                    if (priceFilter === "price highest") {
                      results = results.sort((a, b) => b.price - a.price)
                    } else if (priceFilter === "price lowest") {
                      results = results.sort((a, b) => b.price - a.price).reverse()
                    } else if (priceFilter === "recent") {
                      results = results.sort((a, b) => b.timestamp.localeCompare(a.timestamp))
                    }
                  }

                  if (value !== "") results = results.filter(result => result.audience === value)

                  setFilteredProperties(results)
                }}
                value={typeFilter}
              >
                <option value="" hidden>
                  None
                </option>
                <option value="professional">Professional</option>
                <option value="student">Student</option>
              </select>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 -mr-2">
                <path
                  fillRule="evenodd"
                  d="M10 3a.75.75 0 01.55.24l3.25 3.5a.75.75 0 11-1.1 1.02L10 4.852 7.3 7.76a.75.75 0 01-1.1-1.02l3.25-3.5A.75.75 0 0110 3zm-3.76 9.2a.75.75 0 011.06.04l2.7 2.908 2.7-2.908a.75.75 0 111.1 1.02l-3.25 3.5a.75.75 0 01-1.1 0l-3.25-3.5a.75.75 0 01.04-1.06z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className={`${isSearchOpen ? "hidden" : "flex items-center"} ml-auto text-center gap-x-[0.625rem] min-w-16`}>
              <div className={`gap-4 hidden lg:flex`}>
                <button
                  aria-label="Toggle map view"
                  onClick={() => setView(view => (view === "map" ? "grid" : "map"))}
                  className="p-2 overflow-hidden transition-all duration-300 rounded hover:bg-cpink-50"
                >
                  {view === "map" ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-6 h-6"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-6 h-6"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"
                      />
                    </svg>
                  )}
                </button>
              </div>
              <div className="lg:pl-4 lg:border-l border-stone-900">
                <h1 className="text-md">{filteredProperties.length}</h1>
                <p className="-mt-1 text-sm">{filteredProperties.length === 1 ? "result" : "results"}</p>
              </div>
            </div>
          </div>
          <div className={`relative justify-center flex-1 ${view === "map" ? "hidden lg:flex" : "hidden"} mt-8`}>
            <div className="flex flex-col justify-center w-full h-full gap-8 lg:flex-row lg:max-w-screen-lg sm:max-w-screen-sm md:max-w-screen-md xl:max-w-screen-xl 2xl:max-w-screen-2xl">
              <div
                id="map-container"
                className="flex-[2] bg-cpink rounded lg:sticky lg:top-[6.25rem]"
                style={{ height: "calc(100vh - 8rem)" }}
              >
                <div className="w-full h-[35vh] lg:h-full overflow-hidden rounded relative">
                  {view === "map" && (
                    <Map
                      projection="globe"
                      reuseMaps
                      ref={mapRef}
                      mapboxAccessToken="pk.eyJ1IjoiaHVudGxleXMiLCJhIjoiY2xhY3AyNWR2MGMzcDN1bGRkaGlqdG14eiJ9.gAESyV1HhlLB1dz4hhF-tA"
                      initialViewState={{
                        latitude: 52.76355875612237,
                        longitude: -1.21086378,
                        zoom: 11.75,
                      }}
                      onMove={() => {
                        const { lat, lng } = mapRef.current.getCenter()
                        if (lat.toFixed(4) === (52.76355875612237).toFixed(4) && lng.toFixed(4) === (-1.21086378).toFixed(4)) {
                          setShowRecenter(false)
                        } else {
                          setShowRecenter(true)
                        }
                      }}
                      style={{ width: "100%", height: "100%", position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}
                      mapStyle="mapbox://styles/huntleys/clacpu16j001s14od0751ssv0"
                      // mapStyle="mapbox://styles/mapbox/satellite-v9"
                    >
                      <NavigationControl showCompass={false} />
                      {/* <Source id="my-data" type="geojson" data={geojson}>
                      <Layer {...layerStyle} />
                    </Source> */}
                      {filteredProperties.map((property, index) => {
                        if (index === selectedIndex) {
                          return (
                            <Marker
                              onClick={() => {
                                if (selectedIndex === index) return router.push(`/rent/${property.id}`)

                                setSelectedIndex(index)
                                mapRef?.current?.panTo([property?.long, property?.lat], { zoom: 13 })
                                const newY =
                                  document.getElementById(`button-${index}`).getBoundingClientRect().y -
                                  document.getElementById(`map-buttons-container`).getBoundingClientRect().top +
                                  92

                                window.scrollTo({ top: newY, behavior: "smooth" })
                              }}
                              key={index}
                              longitude={property.long}
                              latitude={property.lat}
                              anchor="bottom"
                            >
                              <img
                                alt="marker"
                                src="/images/marker.svg"
                                className="h-12 opacity-90 drop-shadow-sm hover:cursor-pointer"
                              />
                            </Marker>
                          )
                        }

                        return (
                          <Marker
                            onClick={() => {
                              setSelectedIndex(index)
                              mapRef?.current?.panTo([property?.long, property?.lat], { zoom: 13 })
                              const newY =
                                document.getElementById(`button-${index}`).getBoundingClientRect().y -
                                document.getElementById(`map-buttons-container`).getBoundingClientRect().top +
                                92

                              window.scrollTo({ top: newY, behavior: "smooth" })
                            }}
                            key={index}
                            longitude={property.long}
                            latitude={property.lat}
                            anchor="bottom"
                          >
                            <img
                              alt="peach marker"
                              src="/images/marker-peach.svg"
                              className="h-12 opacity-90 drop-shadow-sm hover:cursor-pointer"
                            />
                          </Marker>
                        )
                      })}
                    </Map>
                  )}
                  <div className="absolute z-50 top-4 left-4">
                    <button
                      aria-label="Filters"
                      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                      className={`flex items-center p-2 px-3 transition-all duration-300 rounded bg-cpink text-stone-900 drop-shadow-sm gap-x-2 ${
                        showFiltersButton ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
                      } `}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                        <path d="M18.75 12.75h1.5a.75.75 0 000-1.5h-1.5a.75.75 0 000 1.5zM12 6a.75.75 0 01.75-.75h7.5a.75.75 0 010 1.5h-7.5A.75.75 0 0112 6zM12 18a.75.75 0 01.75-.75h7.5a.75.75 0 010 1.5h-7.5A.75.75 0 0112 18zM3.75 6.75h1.5a.75.75 0 100-1.5h-1.5a.75.75 0 000 1.5zM5.25 18.75h-1.5a.75.75 0 010-1.5h1.5a.75.75 0 010 1.5zM3 12a.75.75 0 01.75-.75h7.5a.75.75 0 010 1.5h-7.5A.75.75 0 013 12zM9 3.75a2.25 2.25 0 100 4.5 2.25 2.25 0 000-4.5zM12.75 12a2.25 2.25 0 114.5 0 2.25 2.25 0 01-4.5 0zM9 15.75a2.25 2.25 0 100 4.5 2.25 2.25 0 000-4.5z" />
                      </svg>
                      <p>Filters</p>
                    </button>
                  </div>
                  <div className={`absolute z-50 top-[4.875rem] right-2`}>
                    <button
                      aria-label="Recenter map"
                      onClick={() => {
                        setSelectedIndex(-1)
                        mapRef?.current?.panTo([-1.21086378, 52.76355875612237], { zoom: 11.75 })
                      }}
                      className={`w-[2.0875rem] flex items-center justify-center h-[2.0875rem] hover:bg-stone-100 bg-white border-2 rounded-md border-neutral-300 border-opacity-90 transition-all duration-150 ${
                        showRecenter ? "opacity-100" : "opacity-0"
                      }`}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        className="w-5 h-5 text-neutral-800"
                      >
                        <path d="M4.25 2A2.25 2.25 0 002 4.25v2a.75.75 0 001.5 0v-2a.75.75 0 01.75-.75h2a.75.75 0 000-1.5h-2zM13.75 2a.75.75 0 000 1.5h2a.75.75 0 01.75.75v2a.75.75 0 001.5 0v-2A2.25 2.25 0 0015.75 2h-2zM3.5 13.75a.75.75 0 00-1.5 0v2A2.25 2.25 0 004.25 18h2a.75.75 0 000-1.5h-2a.75.75 0 01-.75-.75v-2zM18 13.75a.75.75 0 00-1.5 0v2a.75.75 0 01-.75.75h-2a.75.75 0 000 1.5h2A2.25 2.25 0 0018 15.75v-2zM7 10a3 3 0 116 0 3 3 0 01-6 0z" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
              <div
                id="map-buttons-container"
                ref={mapButtonsContainer}
                className="flex-[1] overflow-hidden max-h-[37.5vh] lg:max-h-[none] lg:flex-[1.75] xl:flex-[1.5] 2xl:flex-[1] h-full lg:overflow-auto no-scrollbar"
                style={{ paddingBottom: mapHeight }}
              >
                {filteredProperties.map((property, index) => (
                  <div className="flex flex-col" key={index}>
                    {indexesOfFirstAd.includes(index) && (
                      <Link
                        key={index}
                        id={`button-${index}`}
                        ref={selectedIndex === index ? mapButtonRef : null}
                        itemType="http://schema.org/Residence"
                        onClick={event => {
                          if (selectedIndex === index) {
                            return
                          }

                          event.preventDefault()

                          document?.body?.classList?.remove("overflow-hidden")
                          setSelectedIndex(index)
                          mapRef?.current?.panTo([property?.long, property?.lat], { zoom: 13 })
                          const newY =
                            document.getElementById(`button-${index}`).getBoundingClientRect().y -
                            document.getElementById(`map-buttons-container`).getBoundingClientRect().top +
                            92

                          window.scrollTo({ top: newY, behavior: "smooth" })
                        }}
                        className={`flex ${
                          selectedIndex === index ? "bg-teal-900 text-stone-100" : "bg-cpink text-stone-900"
                        } text-left relative min-w-full h-48 overflow-hidden transition-all duration-300 rounded snap-start min-h-fit ${
                          index === 0 ? "" : "mt-8"
                        }`}
                        href={`/rent/${property?.id}`}
                      >
                        <div className="flex-[1] h-full relative w-full overflow-hidden">
                          <img
                            alt={`${property?.address}, ${property?.area?.toUpperCase()}`}
                            itemType="http://schema.org/Residence"
                            className={`relative w-full h-full object-cover transition-all duration-500 select-none hover:scale-105 ${
                              property?.is_sold ? "grayscale hover:grayscale-0" : "grayscale-0"
                            }`}
                            src={property?.thumbnail}
                            loading={index < 8 ? "eager" : "lazy"}
                          />
                        </div>
                        <div className="flex-[1.5] flex flex-col">
                          <div className="flex justify-between p-4 pb-0 flex-[1]">
                            <div>
                              <h1>{property?.address?.split(",")?.[0]}</h1>
                              <p className="font-bold">{property?.area}</p>
                            </div>
                            {property?.beds >= 0 && property?.baths >= 0 && (
                              <div className="flex text-center gap-x-4">
                                <div>
                                  <h1 className="text-lg">{property?.beds}</h1>
                                  <p className="-mt-1 text-sm">{property?.beds === 1 ? "bed" : "beds"}</p>
                                </div>
                                <div
                                  className={`w-px transition-all duration-300 ${
                                    selectedIndex === index ? "bg-stone-200" : "bg-stone-900"
                                  }`}
                                ></div>
                                <div>
                                  <h1 className="text-lg">{property?.baths}</h1>
                                  <p className="-mt-1 text-sm">{property?.beds === 1 ? "bath" : "baths"}</p>
                                </div>
                              </div>
                            )}
                          </div>
                          <p className="px-4 pt-2 text-xs">{property?.description}</p>
                          <div className="flex items-center justify-between px-4 pb-4 mt-4 font-medium">
                            <p className="font-normal" style={{ fontSize: 16 }}>
                              {`${new Intl.NumberFormat("en-gb", {
                                style: "currency",
                                currency: "GBP",
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                              }).format(
                                property?.audience === "student" && (property?.price * 12) / 52 / property?.beds > 50
                                  ? (property?.price * 12) / 52 / property?.beds
                                  : property?.price
                              )} ${property?.audience === "student" ? "pppw" : "pcm"}`}
                            </p>
                            <div>
                              {selectedIndex === index ? (
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 24 24"
                                  fill="currentColor"
                                  className="w-5 h-5"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M15 3.75a.75.75 0 01.75-.75h4.5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0V5.56l-3.97 3.97a.75.75 0 11-1.06-1.06l3.97-3.97h-2.69a.75.75 0 01-.75-.75zm-12 0A.75.75 0 013.75 3h4.5a.75.75 0 010 1.5H5.56l3.97 3.97a.75.75 0 01-1.06 1.06L4.5 5.56v2.69a.75.75 0 01-1.5 0v-4.5zm11.47 11.78a.75.75 0 111.06-1.06l3.97 3.97v-2.69a.75.75 0 011.5 0v4.5a.75.75 0 01-.75.75h-4.5a.75.75 0 010-1.5h2.69l-3.97-3.97zm-4.94-1.06a.75.75 0 010 1.06L5.56 19.5h2.69a.75.75 0 010 1.5h-4.5a.75.75 0 01-.75-.75v-4.5a.75.75 0 011.5 0v2.69l3.97-3.97a.75.75 0 011.06 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              ) : (
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  strokeWidth={1.5}
                                  stroke="currentColor"
                                  className="w-5 h-5"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"
                                  />
                                </svg>
                              )}
                            </div>
                          </div>
                        </div>
                      </Link>
                    )}
                    <Link
                      id={`button-${index}`}
                      ref={selectedIndex === index ? mapButtonRef : null}
                      itemType="http://schema.org/Residence"
                      onClick={event => {
                        if ([...indexesOfFirstAd].includes(index)) return

                        if (selectedIndex === index) {
                          return
                        }

                        event.preventDefault()

                        document?.body?.classList?.remove("overflow-hidden")
                        setSelectedIndex(index)
                        mapRef?.current?.panTo([property?.long, property?.lat], { zoom: 13 })
                        const newY =
                          document.getElementById(`button-${index}`).getBoundingClientRect().y -
                          document.getElementById(`map-buttons-container`).getBoundingClientRect().top +
                          92

                        window.scrollTo({ top: newY, behavior: "smooth" })
                      }}
                      className={`flex ${
                        selectedIndex === index ? "bg-teal-900 text-stone-100" : "bg-cpink text-stone-900"
                      } text-left relative min-w-full h-48 overflow-hidden transition-all duration-300 rounded snap-start min-h-fit ${
                        index === 0 ? "" : "mt-8"
                      }`}
                      href={indexesOfFirstAd.includes(index) ? "/valuation?type=let" : `/rent/${property?.id}`}
                    >
                      <div className="flex-[1] h-full relative w-full overflow-hidden">
                        <img
                          alt={`${property?.address}, ${property?.area?.toUpperCase()}`}
                          itemType="http://schema.org/Residence"
                          className={`relative w-full h-full object-cover transition-all duration-500 select-none hover:scale-105 ${
                            property?.is_sold ? "grayscale hover:grayscale-0" : "grayscale-0"
                          }`}
                          src={property?.thumbnail}
                          loading={index < 8 ? "eager" : "lazy"}
                        />
                      </div>
                      <div className="flex-[1.5] flex flex-col">
                        <div className="flex justify-between p-4 pb-0 flex-[1]">
                          <div>
                            <h1>{property?.address?.split(",")?.[0]}</h1>
                            <p className="font-bold">{property?.area}</p>
                          </div>
                          {property?.beds >= 0 && property?.baths >= 0 && (
                            <div className="flex text-center gap-x-4">
                              <div>
                                <h1 className="text-lg">{property?.beds}</h1>
                                <p className="-mt-1 text-sm">{property?.beds === 1 ? "bed" : "beds"}</p>
                              </div>
                              <div
                                className={`w-px transition-all duration-300 ${
                                  selectedIndex === index ? "bg-stone-200" : "bg-stone-900"
                                }`}
                              ></div>
                              <div>
                                <h1 className="text-lg">{property?.baths}</h1>
                                <p className="-mt-1 text-sm">{property?.beds === 1 ? "bath" : "baths"}</p>
                              </div>
                            </div>
                          )}
                        </div>
                        <p className="px-4 pt-2 text-xs">{property?.description}</p>
                        <div className="flex items-center justify-between px-4 pb-4 mt-4 font-medium">
                          <p className="font-normal" style={{ fontSize: 16 }}>
                            {`${new Intl.NumberFormat("en-gb", {
                              style: "currency",
                              currency: "GBP",
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            }).format(
                              property?.audience === "student" && (property?.price * 12) / 52 / property?.beds > 50
                                ? (property?.price * 12) / 52 / property?.beds
                                : property?.price
                            )} ${property?.audience === "student" ? "pppw" : "pcm"}`}
                          </p>
                          <div>
                            {selectedIndex === index ? (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                className="w-5 h-5"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M15 3.75a.75.75 0 01.75-.75h4.5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0V5.56l-3.97 3.97a.75.75 0 11-1.06-1.06l3.97-3.97h-2.69a.75.75 0 01-.75-.75zm-12 0A.75.75 0 013.75 3h4.5a.75.75 0 010 1.5H5.56l3.97 3.97a.75.75 0 01-1.06 1.06L4.5 5.56v2.69a.75.75 0 01-1.5 0v-4.5zm11.47 11.78a.75.75 0 111.06-1.06l3.97 3.97v-2.69a.75.75 0 011.5 0v4.5a.75.75 0 01-.75.75h-4.5a.75.75 0 010-1.5h2.69l-3.97-3.97zm-4.94-1.06a.75.75 0 010 1.06L5.56 19.5h2.69a.75.75 0 010 1.5h-4.5a.75.75 0 01-.75-.75v-4.5a.75.75 0 011.5 0v2.69l3.97-3.97a.75.75 0 011.06 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            ) : (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="w-5 h-5"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"
                                />
                              </svg>
                            )}
                          </div>
                          {indexesOfFirstAd.includes(index) && (
                            <div className="absolute top-0 bottom-0 left-0 right-0 bg-stone-900">
                              <div className="flex-[1] h-full relative w-full overflow-hidden">
                                <img
                                  alt={`${property?.address}, ${property?.area?.toUpperCase()}`}
                                  itemType="http://schema.org/Residence"
                                  className={`relative w-full h-full object-cover transition-all duration-500 select-none hover:scale-105 ${
                                    property?.is_sold ? "hover:grayscale-0" : "grayscale-0"
                                  }`}
                                  src={"/images/ad-2.webp"}
                                />
                                <div className="absolute top-0 bottom-0 left-0 right-0 p-4 bg-opacity-50 pointer-events-none bg-stone-900">
                                  <div className="p-2">
                                    <h1 className="font-serif text-3xl font-bold text-white sm:text-3xl drop-shadow">
                                      Arrange a valuation
                                    </h1>
                                    <p className="mt-3 text-sm font-normal leading-snug text-white">
                                      Discover the true value of your property with Kyfer. He has a great understanding of local
                                      rental market trends, providing accurate and realistic valuations. No commitment required.
                                    </p>
                                  </div>
                                  <div className="flex justify-end mt-4 text-white">
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      strokeWidth={1.5}
                                      stroke="currentColor"
                                      className="w-5 h-5"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5m-9-6h.008v.008H12v-.008zM12 15h.008v.008H12V15zm0 2.25h.008v.008H12v-.008zM9.75 15h.008v.008H9.75V15zm0 2.25h.008v.008H9.75v-.008zM7.5 15h.008v.008H7.5V15zm0 2.25h.008v.008H7.5v-.008zm6.75-4.5h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V15zm0 2.25h.008v.008h-.008v-.008zm2.25-4.5h.008v.008H16.5v-.008zm0 2.25h.008v.008H16.5V15z"
                                      />
                                    </svg>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div
            className={`w-full grid grid-cols-1 mx-auto mt-8 mb-12 transition-all duration-300 ${
              view !== "map" ? "" : "lg:hidden"
            } gap-x-6 gap-y-12 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 sm:grid-cols-2 sm:max-w-screen-sm md:max-w-screen-md lg:max-w-screen-lg xl:max-w-screen-xl 2xl:max-w-screen-2xl min-h-fit`}
          >
            {filteredProperties.map((property, index) => (
              <>
                {indexesOfFirstAd.includes(index) && (
                  <div key={index}>
                    <Link
                      className="relative block min-w-full overflow-hidden transition-all duration-300 rounded h-fit"
                      href={`/valuation?type=let`}
                    >
                      <div className="relative top-0 bottom-0 left-0 right-0 w-full h-full">
                        <div className="aspect-[1.625] w-full overflow-hidden relative">
                          <img
                            alt={`${property?.address}, ${property?.area?.toUpperCase()}`}
                            className={`relative object-cover w-full h-full transition-all duration-500 select-none hover:scale-105`}
                            src={property?.thumbnail}
                            loading={index < 8 ? "eager" : "lazy"}
                          />
                          {property?.audience === "student" && (
                            <div className="absolute flex items-center justify-center p-2 overflow-hidden rounded-full pointer-events-none bottom-4 right-4 bg-stone-50 aspect-square text-stone-900 border-neutral-900">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                className="block w-5 h-5"
                              >
                                <path d="M11.7 2.805a.75.75 0 01.6 0A60.65 60.65 0 0122.83 8.72a.75.75 0 01-.231 1.337 49.949 49.949 0 00-9.902 3.912l-.003.002-.34.18a.75.75 0 01-.707 0A50.009 50.009 0 007.5 12.174v-.224c0-.131.067-.248.172-.311a54.614 54.614 0 014.653-2.52.75.75 0 00-.65-1.352 56.129 56.129 0 00-4.78 2.589 1.858 1.858 0 00-.859 1.228 49.803 49.803 0 00-4.634-1.527.75.75 0 01-.231-1.337A60.653 60.653 0 0111.7 2.805z" />
                                <path d="M13.06 15.473a48.45 48.45 0 017.666-3.282c.134 1.414.22 2.843.255 4.285a.75.75 0 01-.46.71 47.878 47.878 0 00-8.105 4.342.75.75 0 01-.832 0 47.877 47.877 0 00-8.104-4.342.75.75 0 01-.461-.71c.035-1.442.121-2.87.255-4.286A48.4 48.4 0 016 13.18v1.27a1.5 1.5 0 00-.14 2.508c-.09.38-.222.753-.397 1.11.452.213.901.434 1.346.661a6.729 6.729 0 00.551-1.608 1.5 1.5 0 00.14-2.67v-.645a48.549 48.549 0 013.44 1.668 2.25 2.25 0 002.12 0z" />
                                <path d="M4.462 19.462c.42-.419.753-.89 1-1.394.453.213.902.434 1.347.661a6.743 6.743 0 01-1.286 1.794.75.75 0 11-1.06-1.06z" />
                              </svg>
                            </div>
                          )}
                          {property?.pets === 1 && (
                            <div className="absolute flex items-center justify-center p-2 overflow-hidden rounded-full pointer-events-none bottom-4 right-4 bg-stone-50 aspect-square text-stone-900 border-neutral-900">
                              <IoMdPaw className="block w-5 h-5" />
                            </div>
                          )}
                        </div>
                        <div className="absolute top-0 left-0 right-0 p-4 pointer-events-none">
                          <span className="inline-block px-3 py-2 font-medium tracking-wide text-white bg-opacity-75 rounded bg-stone-900">
                            {`${new Intl.NumberFormat("en-gb", {
                              style: "currency",
                              currency: "GBP",
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            }).format(
                              property?.audience === "student" && (property?.price * 12) / 52 / property?.beds > 50
                                ? (property?.price * 12) / 52 / property?.beds
                                : property?.price
                            )} ${property?.audience === "student" ? "pppw" : "pcm"}`}
                          </span>
                        </div>
                        <div className="flex justify-between p-4 bg-cpink text-stone-900">
                          <div>
                            <h1>{property?.address?.split(",")?.[0]}</h1>
                            <p className="font-bold">{property?.area}</p>
                          </div>
                          <div className="flex items-end text-center gap-x-4">
                            {property?.beds > 0 ? (
                              <div>
                                <h1 className="text-lg">{property?.beds}</h1>
                                <p className="-mt-1 text-sm">{property?.beds === 1 ? "bed" : "beds"}</p>
                              </div>
                            ) : (
                              <div>
                                <p className="-mt-1 text-sm">studio</p>
                              </div>
                            )}
                            <div className="w-px h-full bg-stone-900"></div>
                            <div>
                              <h1 className="text-lg">{property?.baths}</h1>
                              <p className="-mt-1 text-sm">{property?.baths === 1 ? "bath" : "baths"}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="absolute top-0 bottom-0 left-0 right-0">
                        <img
                          alt="Arrange a valuation"
                          className={`relative object-cover w-full h-full transition-all duration-500 select-none hover:scale-105`}
                          src={`/images/ad-2.webp`}
                          loading={index < 8 ? "eager" : "lazy"}
                        />
                        <div className="absolute top-0 bottom-0 left-0 right-0 flex flex-col justify-end bg-opacity-75 pointer-events-none bg-gradient-to-b from-transparent to-stone-900">
                          <div className="p-5">
                            <h1 className="font-serif text-2xl font-bold text-white sm:text-3xl drop-shadow">
                              Arrange a valuation
                            </h1>
                            <p className="mt-2 text-xs font-normal leading-snug text-white">
                              Discover the true value of your property with Kyfer. He has a great understanding of local rental
                              market trends, providing accurate and realistic valuations. No commitment required.
                            </p>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </div>
                )}
                <div key={index}>
                  <Link
                    className="relative block min-w-full overflow-hidden transition-all duration-300 rounded h-fit"
                    href={`/rent/${property?.id}`}
                  >
                    <div className="relative top-0 bottom-0 left-0 right-0 w-full h-full">
                      <div className="aspect-[1.625] w-full overflow-hidden relative">
                        <img
                          alt={`${property?.address}, ${property?.area?.toUpperCase()}`}
                          className={`relative object-cover w-full h-full transition-all duration-500 select-none hover:scale-105`}
                          src={property?.thumbnail}
                          loading={index < 8 ? "eager" : "lazy"}
                        />
                        {property?.audience === "student" && (
                          <div className="absolute flex items-center justify-center p-2 overflow-hidden rounded-full pointer-events-none bottom-4 right-4 bg-stone-50 aspect-square text-stone-900 border-neutral-900">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                              className="block w-5 h-5"
                            >
                              <path d="M11.7 2.805a.75.75 0 01.6 0A60.65 60.65 0 0122.83 8.72a.75.75 0 01-.231 1.337 49.949 49.949 0 00-9.902 3.912l-.003.002-.34.18a.75.75 0 01-.707 0A50.009 50.009 0 007.5 12.174v-.224c0-.131.067-.248.172-.311a54.614 54.614 0 014.653-2.52.75.75 0 00-.65-1.352 56.129 56.129 0 00-4.78 2.589 1.858 1.858 0 00-.859 1.228 49.803 49.803 0 00-4.634-1.527.75.75 0 01-.231-1.337A60.653 60.653 0 0111.7 2.805z" />
                              <path d="M13.06 15.473a48.45 48.45 0 017.666-3.282c.134 1.414.22 2.843.255 4.285a.75.75 0 01-.46.71 47.878 47.878 0 00-8.105 4.342.75.75 0 01-.832 0 47.877 47.877 0 00-8.104-4.342.75.75 0 01-.461-.71c.035-1.442.121-2.87.255-4.286A48.4 48.4 0 016 13.18v1.27a1.5 1.5 0 00-.14 2.508c-.09.38-.222.753-.397 1.11.452.213.901.434 1.346.661a6.729 6.729 0 00.551-1.608 1.5 1.5 0 00.14-2.67v-.645a48.549 48.549 0 013.44 1.668 2.25 2.25 0 002.12 0z" />
                              <path d="M4.462 19.462c.42-.419.753-.89 1-1.394.453.213.902.434 1.347.661a6.743 6.743 0 01-1.286 1.794.75.75 0 11-1.06-1.06z" />
                            </svg>
                          </div>
                        )}
                        {property?.pets === 1 && (
                          <div className="absolute flex items-center justify-center p-2 overflow-hidden rounded-full pointer-events-none bottom-4 right-4 bg-stone-50 aspect-square text-stone-900 border-neutral-900">
                            <IoMdPaw className="block w-5 h-5" />
                          </div>
                        )}
                      </div>
                      <div className="absolute top-0 left-0 right-0 p-4 pointer-events-none">
                        <span className="inline-block px-3 py-2 font-medium tracking-wide text-white bg-opacity-75 rounded bg-stone-900">
                          {`${new Intl.NumberFormat("en-gb", {
                            style: "currency",
                            currency: "GBP",
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          }).format(
                            property?.audience === "student" && (property?.price * 12) / 52 / property?.beds > 50
                              ? (property?.price * 12) / 52 / property?.beds
                              : property?.price
                          )} ${property?.audience === "student" ? "pppw" : "pcm"}`}
                        </span>
                      </div>
                      <div className="flex justify-between p-4 bg-cpink text-stone-900">
                        <div>
                          <h1>{property?.address?.split(",")?.[0]}</h1>
                          <p className="font-bold">{property?.area}</p>
                        </div>
                        <div className="flex items-end text-center gap-x-4">
                          {property?.beds > 0 ? (
                            <div>
                              <h1 className="text-lg">{property?.beds}</h1>
                              <p className="-mt-1 text-sm">{property?.beds === 1 ? "bed" : "beds"}</p>
                            </div>
                          ) : (
                            <div>
                              <p className="-mt-1 text-sm">studio</p>
                            </div>
                          )}
                          <div className="w-px h-full bg-stone-900"></div>
                          <div>
                            <h1 className="text-lg">{property?.baths}</h1>
                            <p className="-mt-1 text-sm">{property?.baths === 1 ? "bath" : "baths"}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              </>
            ))}
          </div>
        </div>
        {scrollPosition > windowInnerHeight * 1.5 && (
          <div
            onClick={() => window?.scrollTo(0, 0)}
            className="fixed z-50 items-center justify-center hidden p-2 transition-all duration-300 bg-white border rounded-full border-stone-900 sm:flex right-4 bottom-16 text-stone-900 hover:bg-cpink hover:cursor-pointer drop-shadow hover:scale-125"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-5 h-5"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
            </svg>
          </div>
        )}
        {/* <div className="flex justify-center w-full px-4 py-12 mt-12 bg-cpink">
          <div className="w-full mx-auto sm:max-w-screen-sm md:max-w-screen-md lg:max-w-screen-lg xl:max-w-screen-xl 2xl:max-w-screen-2xl">
            <h1 className="w-full font-serif text-4xl font-semibold bg-cpink text-stone-900 sm:text-left">
              Looking to let your property? <br className="hidden sm:block" />
              Contact us to learn more.
            </h1>
            <Link href="/valuation?type=let">
              <button
                aria-label="Arrange a valuation"
                className="px-4 py-3 mt-6 font-medium transition-all duration-300 border-2 rounded select-none text-stone-900 hover:bg-white border-cpink-200 bg-cpink-200 hover:border-white"
              >
                Arrange a valuation
              </button>
            </Link>
          </div>
        </div> */}
        <section>
          <div className="relative flex bg-white">
            <div className="justify-center hidden w-full mx-auto max-h-64 sm:flex sm:max-w-screen-sm md:max-w-screen-md lg:max-w-screen-lg xl:max-w-screen-xl 2xl:max-w-screen-2xl">
              <div className="absolute bottom-0 left-0 right-0 flex top-12 bg-cpink"></div>
              <div className="z-10 items-center justify-center w-1/4 px-4 -mt-48 sm:-mt-0 sm:-mb-32 sm:w-2/5 md:-mb-48 lg:w-1/6 xl:w-2/7">
                <img alt="Phone" className="w-full select-none" src="/images/phone-3.png" />
                <img alt="Laptop" className="w-full select-none sm:hidden" src="/images/phone-3.png" />
              </div>
              <div className="z-10 flex w-3/5 pt-12 md:py-12 md:pt-[4.75rem]">
                <div className="px-4 my-auto">
                  <h2 className="w-full font-serif text-4xl font-semibold text-stone-900 sm:text-left drop-shadow">
                    Find out what your property is worth
                  </h2>
                  <Link href="/valuation?type=let" target="_blank">
                    <button className="px-4 py-3 mt-6 font-medium transition-all duration-300 border-2 rounded select-none text-stone-900 bg-cpink-200 border-cpink-200 hover:bg-white hover:border-white">
                      Arrange a valuation
                    </button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white sm:hidden">
            <div className="flex justify-center px-4 sm:hidden">
              <img alt="Laptop" className="w-full max-w-sm select-none" src="/images/phone-3.png" />
            </div>
            <div className="relative px-4 -mt-96 bg-cpink">
              <div className="flex py-12 ">
                <div className="flex-1 min-w-0 sm:flex-2">
                  <h2 className="w-full font-serif text-4xl font-semibold text-stone-900 sm:text-left drop-shadow">
                    Find out what your property is worth
                  </h2>
                  <Link href="/valuation?type=let" target="_blank">
                    <button className="px-4 py-3 mt-6 font-medium transition-all duration-300 border-2 rounded select-none text-stone-900 bg-cpink-200 border-cpink-200 hover:bg-white hover:border-white">
                      Arrange a Valuation
                    </button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
        <Footer />
        <Menu />
      </div>
    </>
  )
}

export default Rent
